/** @format */
import { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import React, {
    Fragment,
    FunctionComponent,
    useContext,
    useEffect,
} from 'react';

import { DataLayerEventType } from '@toucan/types/analytics';
import { ProductConditionValue } from '@toucan/types/product';

import product from '@toucan/custom-server/services/product';

import {
    MetaFactoryType,
    metaFactories,
} from '@toucan/services/factories/meta';

import {
    trackViaDataLayer,
    transformItemToDataLayerItem,
} from '@toucan/helpers/analytics';
import { centToDecimal } from '@toucan/helpers/currency';
import { getServerSideProps as getSharedServerSideProps } from '@toucan/helpers/next/page';

import { Product as ProductView } from '@toucan/components/views/product';

import { Layout, LayoutType } from '@toucan/components/organisms/layout';

import { getMetaElements } from '@toucan/components/utility/seo/meta';

import { MarketContext } from '@toucan/client/global/market';
import { useProductData } from '@toucan/features/product/hooks/use-product-data';
import { useRecentlyViewedProducts } from '@toucan/features/product/hooks/use-recently-viewed-products';

export type ProductPageProps = NonNullable<
    Awaited<ReturnType<typeof getSharedServerSideProps>>
>['props'] &
    Awaited<ReturnType<typeof product.getPageProps>>;

const defaultTestId = 'product-page';

const Product: FunctionComponent<ProductPageProps> = ({
    modelInfo,
    pageData,
    breadcrumbs,
    parentCategory,
    productInfo: initialProductInfo,
    stockInfo: initialStockInfo,
    modelRecommendations,
    otherStock,
}) => {
    const { activeMarket } = useContext(MarketContext);
    const { t } = useTranslation();
    const { productInfo, stockInfo, refreshProductData } = useProductData({
        initialProductInfo,
        initialStockInfo,
    });
    const { addRecentlyViewedProduct } = useRecentlyViewedProducts();

    const isNewProduct = productInfo.condition === ProductConditionValue.NEW;

    useEffect(() => {
        trackViaDataLayer({
            event: DataLayerEventType.PRODUCT_PAGE_VIEW,
            productId: isNewProduct ? null : productInfo.id,
            productSku: isNewProduct ? null : productInfo.sku,
            modelId: productInfo.modelId,
            modelName: productInfo.name,
            productValue: centToDecimal(productInfo.value),
            items: [
                transformItemToDataLayerItem({
                    name: productInfo.name,
                    id: productInfo.id,
                    currency: activeMarket.currency,
                    value: productInfo.value,
                    category: productInfo.primaryCategoryName,
                    condition: productInfo.condition,
                    brand: modelInfo.brand.name,
                }),
            ],
        });

        if (!isNewProduct) {
            addRecentlyViewedProduct(productInfo.id);
        }
    }, []);

    const metaElements = getMetaElements({
        factory: metaFactories[MetaFactoryType.PRODUCT](
            t,
            modelInfo.name,
            productInfo.condition === ProductConditionValue.NEW
        ),
        ignoreMount: true,
    });

    return (
        <Fragment>
            <Head>{metaElements}</Head>
            <ProductView
                data-testid={`${defaultTestId}__view`}
                productInfo={productInfo}
                pageData={pageData}
                stockInfo={stockInfo}
                breadcrumbs={breadcrumbs}
                parentCategory={parentCategory}
                modelInfo={modelInfo}
                modelRecommendations={modelRecommendations}
                otherStock={otherStock}
                refreshProductData={refreshProductData}
            />
        </Fragment>
    );
};

const ProductPage: NextPage<ProductPageProps> = (props) => {
    return (
        <Layout
            layoutType={LayoutType.FULL}
            data-testid={defaultTestId}
            {...props.layoutProps}
        >
            <Product {...props} />
        </Layout>
    );
};

const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { props: sharedProps } =
        (await getSharedServerSideProps(context)) ?? {};

    const { query } = context;

    const productPageProps = await product.getPageProps(query, context);

    return {
        props: {
            ...sharedProps,
            ...productPageProps,
        },
    };
};

export { getServerSideProps };
export default ProductPage;
